import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, MessageSquare, RefreshCw, Send } from 'lucide-react';
import { TopupOrderService } from '../../services/topupOrderService';

const formatDate = (value) => {
  if (!value) return 'A l instant';

  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Date inconnue';
  }

  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getRoleLabel = (role) => {
  if (role === 'admin') return 'Admin';
  if (role === 'operator') return 'Operateur';
  return 'Revendeur';
};

const getMessageTypeLabel = (messageType) => {
  if (messageType === TopupOrderService.MESSAGE_TYPES.PRODUCT_INFO) return 'Infos produit';
  if (messageType === TopupOrderService.MESSAGE_TYPES.REPLACEMENT) return 'Remplacement';
  if (messageType === TopupOrderService.MESSAGE_TYPES.STATUS_UPDATE) return 'Mise a jour';
  if (messageType === TopupOrderService.MESSAGE_TYPES.CUSTOMER_ISSUE) return 'Probleme';
  return 'Message';
};

const getSenderDisplayName = (message = {}) => {
  if (String(message.id || '').trim() === 'legacy-admin-note') {
    return String(message.senderName || 'Historique admin').trim() || 'Historique admin';
  }

  return TopupOrderService.resolveCanonicalSenderName(message.senderRole);
};

const TopupOrderConversation = ({
  order,
  currentUser,
  userProfile,
  addNotification,
  isStaff = false,
  canSendMessages = true,
  className = ''
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState('');
  const [staffMessageType, setStaffMessageType] = useState(TopupOrderService.MESSAGE_TYPES.STAFF_REPLY);

  const loadMessages = useCallback(async () => {
    const orderId = String(order?.id || '').trim();
    if (!orderId) {
      setMessages([]);
      return;
    }

    try {
      setLoading(true);
      const nextMessages = await TopupOrderService.getOrderMessages(orderId);
      setMessages(nextMessages);
    } catch (error) {
      console.error('Erreur lors du chargement de la conversation Topup:', error);
      addNotification?.('error', error.message || 'Impossible de charger la conversation');
    } finally {
      setLoading(false);
    }
  }, [addNotification, order?.id]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const legacyAdminMessage = useMemo(() => {
    const adminNote = String(order?.adminNote || '').trim();
    if (!adminNote) {
      return null;
    }

    const alreadyMirrored = messages.some((message) => (
      String(message.text || '').trim() === adminNote &&
      ['admin', 'operator'].includes(String(message.senderRole || '').trim())
    ));

    if (alreadyMirrored) {
      return null;
    }

    return {
      id: 'legacy-admin-note',
      senderId: String(order?.processedBy || '').trim() || 'legacy-admin',
      senderRole: 'admin',
      senderName: 'Historique admin',
      text: adminNote,
      messageType: TopupOrderService.MESSAGE_TYPES.STATUS_UPDATE,
      createdAt: order?.processedAt || order?.updatedAt || order?.createdAt || null
    };
  }, [messages, order?.adminNote, order?.createdAt, order?.processedAt, order?.processedBy, order?.updatedAt]);

  const conversationItems = useMemo(() => {
    return legacyAdminMessage ? [legacyAdminMessage, ...messages] : messages;
  }, [legacyAdminMessage, messages]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const text = String(draft || '').trim();
    const orderId = String(order?.id || '').trim();
    if (!canSendMessages || !text || !orderId || !currentUser?.uid) {
      return;
    }

    const senderRole = String(userProfile?.role || '').trim() || 'reseller';
    const senderName = TopupOrderService.resolveCanonicalSenderName(senderRole);
    const messageType = isStaff
      ? staffMessageType
      : TopupOrderService.MESSAGE_TYPES.CUSTOMER_ISSUE;

    try {
      setSubmitting(true);
      await TopupOrderService.sendOrderMessage(orderId, {
        senderId: currentUser.uid,
        senderRole,
        senderName,
        text,
        messageType
      });

      setDraft('');
      if (isStaff) {
        setStaffMessageType(TopupOrderService.MESSAGE_TYPES.STAFF_REPLY);
      }
      await loadMessages();
      addNotification?.('success', 'Message envoye');
    } catch (error) {
      console.error('Erreur lors de l envoi du message Topup:', error);
      addNotification?.('error', error.message || 'Impossible d envoyer le message');
    } finally {
      setSubmitting(false);
    }
  };

  const currentUserId = String(currentUser?.uid || '').trim();
  const canSubmit = canSendMessages && !submitting && Boolean(String(draft || '').trim());

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 ${className}`}>
      <div className="mb-3 flex items-start justify-between gap-3 sm:mb-4 sm:items-center">
        <div className="flex min-w-0 items-center gap-2">
          <MessageSquare className="h-5 w-5 text-slate-500" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Conversation</p>
            <p className="text-xs text-slate-500">Historique complet entre revendeur et staff</p>
          </div>
        </div>

        <button
          type="button"
          onClick={loadMessages}
          disabled={loading}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-2.5 py-2 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:px-3 sm:text-xs"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      <div className="max-h-[38dvh] space-y-2 overflow-y-auto rounded-2xl bg-slate-50 p-2.5 sm:max-h-[26rem] sm:space-y-3 sm:p-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement de la conversation...
          </div>
        ) : conversationItems.length > 0 ? (
          conversationItems.map((message) => {
            const isCurrentUser = currentUserId && String(message.senderId || '').trim() === currentUserId;
            const isStaffMessage = ['admin', 'operator'].includes(String(message.senderRole || '').trim());

            return (
              <div
                key={message.id}
                className={`rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 ${
                  isCurrentUser
                    ? 'bg-blue-600 text-white'
                    : isStaffMessage
                      ? 'bg-amber-50 text-slate-900'
                      : 'bg-white text-slate-900'
                }`}
              >
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <span className={`text-[11px] font-semibold sm:text-xs ${isCurrentUser ? 'text-blue-100' : 'text-slate-600'}`}>
                    {getSenderDisplayName(message) || getRoleLabel(message.senderRole)}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-[11px] ${
                    isCurrentUser
                      ? 'bg-white/15 text-blue-50'
                      : isStaffMessage
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-600'
                  }`}>
                    {getMessageTypeLabel(message.messageType)}
                  </span>
                  <span className={`text-[10px] sm:text-[11px] ${isCurrentUser ? 'text-blue-100' : 'text-slate-400'}`}>
                    {formatDate(message.createdAt)}
                  </span>
                </div>

                <p className={`mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 sm:mt-2 ${isCurrentUser ? 'text-white' : 'text-slate-800'}`}>
                  {message.text}
                </p>
              </div>
            );
          })
        ) : (
          <div className="py-6 text-center text-sm text-slate-500">
            Aucun message pour le moment.
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="mt-3 space-y-3 sm:mt-4">
        {isStaff && !canSendMessages ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Seul l agent assigne ou un administrateur peut envoyer des messages sur cette commande.
          </div>
        ) : null}

        {isStaff ? (
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Type de reponse
            </label>
            <select
              value={staffMessageType}
              onChange={(event) => setStaffMessageType(event.target.value)}
              disabled={!canSendMessages}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50"
            >
              <option value={TopupOrderService.MESSAGE_TYPES.STAFF_REPLY}>Reponse</option>
              <option value={TopupOrderService.MESSAGE_TYPES.PRODUCT_INFO}>Infos produit</option>
              <option value={TopupOrderService.MESSAGE_TYPES.REPLACEMENT}>Remplacement</option>
              <option value={TopupOrderService.MESSAGE_TYPES.STATUS_UPDATE}>Mise a jour</option>
            </select>
          </div>
        ) : null}

        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">
            {isStaff ? 'Votre message' : 'Expliquer le probleme'}
          </label>
          <textarea
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={!canSendMessages}
            placeholder={isStaff ? 'Ecrire une reponse pour le revendeur...' : 'Exemple : le compte Netflix a expire ou ne fonctionne pas.'}
            className="w-full resize-none rounded-2xl border border-slate-300 px-3.5 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-50 sm:px-4"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer
          </button>
        </div>
      </form>
    </div>
  );
};

export default TopupOrderConversation;
